import { api, type CreateWorkPack, type CsStanSpec, type PackCandidate, type ReadTaskLine, type WorkPackContext } from '../api';
import { callout, field, input, opt, openDialog, select, val, numVal } from '../dialog';
import { esc, fmtDate, fmtHours, fmtMargin, html, pill, STRIPE, toast } from '../ui';

/**
 * New work pack.
 *
 * Ground time is the expensive part of maintenance, so the dialog's real job is
 * to show everything that could be done on this aircraft in one visit and let
 * the planner tick it. Items are pulled from the live due list and the open
 * defects — nothing is typed twice — and anything outside its tolerance band
 * arrives already ticked, because that is what the visit is for.
 *
 * Changing the aircraft refetches the candidates rather than filtering a list
 * held here: the due list moves whenever a flight is filed, and a stale pick
 * list is how an item gets missed.
 */

const PERFORMERS = [
  { org: 'Independent certifying staff', approval: 'ML.A.801(b)(2)' },
  { org: 'Pilot-owner', approval: 'ML Appendix II' },
];

export function openNewWorkPack(ctx: WorkPackContext, onDone?: () => void): void {
  // The one the candidates belong to, not simply the first on the list —
  // opening this from an aircraft's Programme tab must select that aircraft.
  let aircraftId = ctx.aircraftId ?? ctx.aircraft[0]?.id ?? '';
  let candidates = ctx.candidates;
  // Ticked state survives a re-render; keyed by kind+id so a task and a defect
  // can never collide.
  let ticked = new Set(candidates.filter((c) => c.preselected).map(key));
  let loading = false;

  /**
   * CS-STAN, kept apart from the due list on purpose.
   *
   * Nothing here is due. A standard change is an option the owner may take,
   * with no deadline and nothing overdue if it is never embodied — so mixing
   * sixty-six of them into "items due" would bury the work that actually is.
   * It is a separate, folded section: found when looked for, invisible when not.
   */
  let csStanEntries: CsStanSpec[] = [];
  let csStanEligible = true;
  let tickedSpecs = new Set<string>();
  // Remembered across renders. Ticking a box refreshes the dialog, which builds
  // the <details> afresh — and an element with no `open` attribute comes back
  // shut, so every tick folded the list the user was working through.
  let csStanOpen = false;
  let csStanScroll = 0;

  /**
   * Lines read off a PDF, kept apart from the candidates.
   *
   * A candidate is something this aircraft already owes — a task on its
   * programme, an open defect — with an id the server will check belongs to
   * it. A line off a job card is none of that: it is words the planner chose
   * to bring in, and it goes on the pack as an ad-hoc item that carries its
   * own description. Ticked by default, because the planner uploaded the
   * document to use it; untick what is not this visit.
   */
  let pdfLines: Array<{ id: string; line: ReadTaskLine; fileName: string }> = [];
  let tickedPdf = new Set<string>();
  /** What the document called itself, offered as the pack's title when none is typed. */
  let pdfTitle: string | null = null;

  function key(c: PackCandidate) {
    return `${c.kind}:${c.refId}`;
  }

  async function loadCsStan(id: string) {
    if (!id) return;
    try {
      const cat = await api.standardChanges.catalogue(id);
      csStanEntries = cat.entries;
      csStanEligible = cat.eligibility.eligible;
    } catch {
      // Never blocks building a pack: the catalogue is reference data, and an
      // aircraft with routine work due must not be held up because it could
      // not be fetched.
      csStanEntries = [];
      csStanEligible = false;
    }
  }

  function csStanRows(): string {
    if (!csStanEligible) {
      return html`<p class="tiny muted" style="padding:8px 2px">
        CS-STAN does not reach this aircraft, so a change on it needs an approved design change.
      </p>`;
    }
    if (!csStanEntries.length) {
      return html`<p class="tiny muted" style="padding:8px 2px">
        The CS-STAN catalogue has not been imported on this installation.
      </p>`;
    }
    return csStanEntries
      .map((e) => {
        const notes = [
          e.pilotOwnerMayRelease === false ? 'not for pilot-owner release' : null,
          e.needsAfmSupplement ? 'AFM supplement' : null,
          e.affectsWeightAndBalance ? 'affects weight and balance' : null,
        ].filter(Boolean);
        return html`
          <label>
            <input type="checkbox" data-spec-pick="${esc(e.id)}" ${tickedSpecs.has(e.id) ? 'checked' : ''}>
            <span class="pb">
              <span class="pt"><span style="font-family:var(--mono)">${esc(e.id)}</span> ${esc(e.title)}</span>
              ${notes.length ? html`<span class="pm">${esc(notes.join(' · '))}</span>` : ''}
            </span>
          </label>
        `;
      })
      .join('');
  }

  const performerOptions = () =>
    [
      ...ctx.approvals.map((a) => opt(`${a.reference}|${a.kind}`, `${a.reference} — ${a.kind}`)),
      ...PERFORMERS.map((p) => opt(`${p.approval}|${p.org}`, `${p.org} (${p.approval})`)),
    ].join('');

  const totals = () => {
    const picked = candidates.filter((c) => ticked.has(key(c)));
    const pdfPicked = pdfLines.filter((p) => tickedPdf.has(p.id));
    const hours = picked.reduce((n, c) => n + c.manHours, 0) + pdfPicked.reduce((n, p) => n + (p.line.manHours ?? 0), 0);
    return {
      // Standard changes count towards the pack — they are lines on it — but
      // carry no hours: CS-STAN says what a change is, not how long it takes.
      // Counting only the due items showed "0 items" on a pack that was about
      // to be created with one.
      count: picked.length + pdfPicked.length + tickedSpecs.size,
      hours,
      cost: hours * ctx.labourRate,
    };
  };

  /**
   * What the pack is being built from, and what has to be asked for.
   *
   * Defects and anything already picked from the programme are the visit;
   * three hundred programme lines are a catalogue. Mixing them meant opening
   * the dialog on a list nobody could read, with the whole hundred-hour
   * inspection already ticked before anyone had decided this was that visit.
   */
  const fromProgramme = (c: PackCandidate) => c.kind === 'TASK';
  const inPack = () => candidates.filter((c) => !fromProgramme(c) || ticked.has(key(c)));
  const programmeItems = () => candidates.filter(fromProgramme);

  const marginOf = (c: PackCandidate) =>
    c.remaining === null ? fmtDate(c.dueDate) : fmtMargin(c.remaining, c.unit === 'HOURS' ? 'HOURS' : 'DAYS');

  function candidateRows(): string {
    if (loading) return '<label><span class="pb"><span class="pt">Loading what is due…</span></span></label>';
    const shown = inPack();
    if (!shown.length && !pdfLines.length) {
      return html`<label><span class="pb">
        <span class="pt">Nothing open on this aircraft</span>
        <span class="pm">Scheduled work comes in from the programme — the button above.</span>
      </span></label>`;
    }
    return shown
      .map((c) => {
        const k = key(c);
        return html`
          <label>
            <input type="checkbox" data-pick="${esc(k)}" ${ticked.has(k) ? 'checked' : ''}>
            <span class="pb">
              <span class="pt">${esc(c.title)}</span>
              <span class="pm">${esc([c.detail, marginOf(c), `est. ${c.manHours.toFixed(1)} h`].filter(Boolean).join(' · '))}</span>
            </span>
          </label>
        `;
      })
      .join('') + pdfLines.map((p) => html`
        <label>
          <input type="checkbox" data-pdf-pick="${esc(p.id)}" ${tickedPdf.has(p.id) ? 'checked' : ''}>
          <span class="pb">
            <span class="pt">${esc(lineTitle(p.line))}</span>
            <span class="pm">${esc([p.line.section, p.line.reference, `from ${p.fileName}`, p.line.manHours ? `est. ${p.line.manHours.toFixed(1)} h` : 'no estimate'].filter(Boolean).join(' · '))}</span>
          </span>
        </label>
      `).join('');
  }

  /** A line's first words, with its number in front where the document gave one. */
  const lineTitle = (l: ReadTaskLine) => `${l.code ? `${l.code} ` : ''}${l.description}`;

  /** What goes on the pack for a line: number, citation and the words, on one line. */
  const lineDescription = (l: ReadTaskLine) =>
    [l.code, l.reference].filter(Boolean).join(' · ') + (l.code || l.reference ? ' — ' : '') + l.description;

  /**
   * A task list off a PDF — a job card, a check card, a work instruction.
   *
   * The same shape as the programme picker, because it answers the same
   * question from a different source: here is what the document lists, tick
   * what this visit does. The document is read by the same reader that reads
   * manuals, but nothing is filed and nothing is created until Add is
   * pressed — the lines are held in this dialog and go onto the pack with it.
   */
  function pickFromPdf(refresh: () => void): void {
    let reading: string | null = null;
    let result: { fileName: string; title: string | null; revision: string | null; registration: string | null; pages: number; lines: ReadTaskLine[] } | null = null;
    let chosen = new Set<number>();

    const groups = () => {
      const g = new Map<string, Array<{ i: number; line: ReadTaskLine }>>();
      (result?.lines ?? []).forEach((line, i) => {
        const k = line.section ?? 'Tasks';
        g.set(k, [...(g.get(k) ?? []), { i, line }]);
      });
      return g;
    };

    openDialog<Record<string, unknown>>({
      wide: true,
      title: 'Add from a PDF',
      sub: 'A job card, a check card, a work instruction — read, then tick what this visit does',
      primary: 'Add the ticked tasks',
      body: () => {
        if (reading) {
          return html`<div class="card"><div class="card-b" style="display:flex;align-items:center;gap:10px;color:var(--ink-3)">
            <span class="lbl">Reading ${esc(reading)}…</span>
            <span class="tiny dim">a few pages take about half a minute</span>
          </div></div>`;
        }
        if (!result) {
          return html`
            <div class="fset">
              <div class="lg">The document</div>
              <label class="btn filepick"><input type="file" accept="application/pdf" data-pdf-file> Choose a PDF</label>
              <span class="hint">Up to forty pages. Every task on it is offered, in the document's own words and language;
                nothing is created until you add them. The file itself is not kept — it is being transcribed, not filed.</span>
            </div>
            ${callout('info', 'i', html`For a manual, or a check that repeats, use <b>Build from the manuals</b> on the aircraft's
              Programme tab instead: a task on the programme is forecast and comes onto every pack from <b>Add from AMP</b>,
              where a line added here is this visit's only.`)}
          `;
        }
        const g = groups();
        return html`
          <p class="tiny dim" style="margin:0 0 10px">
            <b>${esc(result.fileName)}</b> · ${result.pages} page${result.pages === 1 ? '' : 's'} · ${result.lines.length} task${result.lines.length === 1 ? '' : 's'}
            ${result.title ? ` · ${esc(result.title)}` : ''}${result.revision ? ` · ${esc(result.revision)}` : ''}${result.registration ? ` · ${esc(result.registration)}` : ''}
            <button type="button" class="btn sm" data-pdf-another style="margin-left:8px">Choose another</button>
          </p>
          ${result.registration && !ctx.aircraft.some((a) => a.id === aircraftId && a.registration.replace(/[^A-Z0-9]/gi, '').toUpperCase() === result!.registration!.replace(/[^A-Z0-9]/gi, '').toUpperCase())
            ? callout('caution', '!', html`The document names <b>${esc(result.registration)}</b>; this pack is for
                <b>${esc(ctx.aircraft.find((a) => a.id === aircraftId)?.registration ?? '')}</b>. Check that is what you mean.`)
            : ''}
          ${[...g.entries()].map(([section, rows]) => html`
            <details class="card" style="margin-bottom:8px" open>
              <summary class="card-h">
                <div class="sum">
                  <span class="twist" aria-hidden="true"></span>
                  <div><h2 style="font-size:13px">${esc(section)}</h2><p class="tiny dim">${rows.length} task${rows.length === 1 ? '' : 's'}</p></div>
                </div>
                <span>
                  <button type="button" class="btn sm" data-group-tick="${esc(section)}">All</button>
                  <button type="button" class="btn sm" data-group-untick="${esc(section)}">None</button>
                </span>
              </summary>
              <div class="scrollx">
                <table class="tbl">
                  <thead><tr><th style="width:34px"></th><th style="width:60px">#</th><th class="col-opt">Reference</th><th class="wide">Task</th><th class="col-opt">Page</th></tr></thead>
                  <tbody>
                    ${rows.map(({ i, line }) => html`
                      <tr>
                        <td><input type="checkbox" data-pdf-pick="${i}" ${chosen.has(i) ? 'checked' : ''}></td>
                        <td class="n">${esc(line.code ?? '—')}</td>
                        <td class="col-opt tiny">${esc(line.reference ?? '—')}</td>
                        <td class="wide">${esc(line.description)}${line.manHours ? html`<span class="sub2">est. ${line.manHours.toFixed(1)} h</span>` : ''}</td>
                        <td class="n col-opt">${line.page ?? '—'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </details>
          `).join('')}
        `;
      },
      onRender: (root, refreshPicker) => {
        root.querySelector<HTMLInputElement>('[data-pdf-file]')?.addEventListener('change', async (e) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (!file) return;
          reading = file.name;
          refreshPicker();
          try {
            const r = await api.planning.readTasks(file);
            result = { fileName: r.document.fileName, title: r.title, revision: r.revision, registration: r.registration, pages: r.document.pages, lines: r.tasks };
            chosen = new Set(r.tasks.map((_, i) => i));
            if (!r.tasks.length) toast('Nothing on that document read as a task.', 'error');
          } catch (err) {
            toast(err instanceof Error ? err.message : 'The document could not be read.', 'error');
          } finally {
            reading = null;
            refreshPicker();
          }
        });
        root.querySelector('[data-pdf-another]')?.addEventListener('click', () => { result = null; chosen = new Set(); refreshPicker(); });
        root.querySelectorAll<HTMLInputElement>('[data-pdf-pick]').forEach((box) =>
          box.addEventListener('change', () => { const i = Number(box.dataset.pdfPick); box.checked ? chosen.add(i) : chosen.delete(i); }),
        );
        const bulk = (attr: 'groupTick' | 'groupUntick', on: boolean) =>
          root.querySelectorAll<HTMLElement>(`[data-${attr === 'groupTick' ? 'group-tick' : 'group-untick'}]`).forEach((b) =>
            b.addEventListener('click', (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              for (const { i } of groups().get(b.dataset[attr]!) ?? []) on ? chosen.add(i) : chosen.delete(i);
              root.querySelectorAll<HTMLInputElement>('[data-pdf-pick]').forEach((box) => { box.checked = chosen.has(Number(box.dataset.pdfPick)); });
            }),
          );
        bulk('groupTick', true);
        bulk('groupUntick', false);
      },
      collect: () => {
        if (reading) return { error: `${reading} is still being read — a moment.` };
        if (!result) return { error: 'Choose a PDF first.' };
        if (!chosen.size) return { error: 'Tick at least one task, or cancel.' };
        return { count: chosen.size };
      },
      submit: async (payload) => payload,
      done: (r) => `${r.count} task(s) from ${result?.fileName ?? 'the document'} on the pack`,
      onDone: () => {
        if (!result) return;
        const stamp = Date.now();
        const added = [...chosen].sort((a, b) => a - b).map((i) => ({ id: `pdf:${stamp}:${i}`, line: result!.lines[i], fileName: result!.fileName }));
        pdfLines = [...pdfLines, ...added];
        for (const p of added) tickedPdf.add(p.id);
        if (!pdfTitle && result.title) pdfTitle = result.title;
        refresh();
      },
    });
  }

  /**
   * The programme, a manual at a time.
   *
   * Its own dialog because it is a different question from "what is this
   * visit": here the planner is reading a schedule — which manual, what
   * interval, how much margin is left on each line — and the margin is the
   * column they are reading it by, so it is the one shown.
   */
  function pickFromProgramme(refresh: () => void): void {
    const items = programmeItems();
    if (!items.length) {
      return void toast('This aircraft has no maintenance programme tasks to draw from.', 'error');
    }

    // Grouped by the manual, ordered by what is closest to due inside each.
    const groups = new Map<string, PackCandidate[]>();
    for (const c of [...items].sort((a, b) => (a.remaining ?? 1e9) - (b.remaining ?? 1e9))) {
      const g = c.source ?? 'Maintenance programme';
      groups.set(g, [...(groups.get(g) ?? []), c]);
    }

    // Its own set, so cancelling the picker leaves the pack as it was.
    const chosen = new Set([...ticked].filter((k) => k.startsWith('TASK:')));

    openDialog<Record<string, unknown>>({
      wide: true,
      title: 'Add from the maintenance programme',
      sub: `${items.length} task(s) on the programme · due first`,
      primary: 'Add the ticked tasks',
      body: () => [...groups.entries()].map(([manual, rows]) => html`
        <details class="card" style="margin-bottom:8px" ${rows.some((r) => r.state !== 'OK') ? 'open' : ''}>
          <summary class="card-h">
            <div class="sum">
              <span class="twist" aria-hidden="true"></span>
              <div>
                <h2 style="font-size:13px">${esc(manual)}</h2>
                <p class="tiny dim">
                  ${rows.length} task(s) · ${rows.filter((r) => r.state !== 'OK').length} due or overdue
                </p>
              </div>
            </div>
            <button type="button" class="btn sm" data-group-all="${esc(manual)}">Tick all due</button>
          </summary>
          <div class="scrollx">
            <table class="tbl">
              <thead>
                <tr>
                  <th style="width:34px"></th>
                  <th class="wide">Task</th>
                  <th class="col-opt">Interval</th>
                  <th class="col-opt">Next due</th>
                  <th>Margin</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                ${rows.map((c) => {
                  const k = key(c);
                  const state = c.state === 'OVERDUE' ? 'OVERDUE' : c.state === 'DUE_SOON' ? 'DUE_SOON' : 'OK';
                  return html`
                    <tr>
                      <td><input type="checkbox" data-amp-pick="${esc(k)}" ${chosen.has(k) ? 'checked' : ''}></td>
                      <td class="st ${STRIPE[state]} wide">
                        ${esc(c.title)}<span class="sub2">${esc(c.detail)}</span>
                      </td>
                      <td class="n col-opt">${esc(c.interval ?? '—')}</td>
                      <td class="n col-opt">
                        ${c.dueTsnHours != null ? `at ${fmtHours(c.dueTsnHours)} h` : esc(fmtDate(c.dueDate))}
                      </td>
                      <td class="n">${esc(marginOf(c))}</td>
                      <td>${pill(state, state === 'OVERDUE' ? 'Overdue' : state === 'DUE_SOON' ? 'Due soon' : 'In tolerance')}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        </details>
      `).join(''),
      onRender: (root) => {
        root.querySelectorAll<HTMLInputElement>('[data-amp-pick]').forEach((box) =>
          box.addEventListener('change', () => {
            const k = box.dataset.ampPick!;
            box.checked ? chosen.add(k) : chosen.delete(k);
          }),
        );
        root.querySelectorAll<HTMLElement>('[data-group-all]').forEach((b) =>
          b.addEventListener('click', (ev) => {
            // Inside a <summary>: without this the click folds the group it
            // has just ticked.
            ev.preventDefault();
            ev.stopPropagation();
            for (const c of groups.get(b.dataset.groupAll!) ?? []) {
              if (c.state !== 'OK') chosen.add(key(c));
            }
            root.querySelectorAll<HTMLInputElement>('[data-amp-pick]').forEach((box) => {
              box.checked = chosen.has(box.dataset.ampPick!);
            });
          }),
        );
      },
      collect: () => ({ chosen: [...chosen] }),
      submit: async (payload) => payload,
      done: () => `${chosen.size} task(s) on the pack`,
      onDone: () => {
        for (const k of [...ticked]) if (k.startsWith('TASK:')) ticked.delete(k);
        for (const k of chosen) ticked.add(k);
        refresh();
      },
    });
  }

  openDialog<CreateWorkPack>({
    wide: true,
    title: 'New work pack',
    sub: 'Group everything that can be done in one visit — ground time is the expensive part',
    primary: 'Create work pack',

    body: () => {
      const t = totals();
      const programmeFromPack = candidates.filter((c) => c.kind === 'TASK' && ticked.has(key(c))).length;
      const money = t.cost.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return html`
        <div class="frow3">
          ${field('Aircraft', select('aircraftId', ctx.aircraft.map((a) => opt(a.id, `${a.registration} — ${a.typeModel}`, a.id === aircraftId)).join(''), 'data-aircraft'))}
          ${field('Reference', input('reference', ctx.reference, 'readonly style="font-family:var(--mono)"'), 'Allocated when the pack is created')}
          ${field('Performing organisation', select('performer', performerOptions()))}
        </div>
        ${field('Title', input('title', '', 'placeholder="100-hour inspection + deferred defects" maxlength="300"'))}
        <div class="frow3">
          ${field('Planned start', input('plannedStart', '', 'type="date"'))}
          ${field('Planned end', input('plannedEnd', '', 'type="date"'))}
          ${field('Ground time (days)', input('groundTimeDays', '1', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        <div>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:7px">
            <div class="lbl">Items due — tick what goes into this visit</div>
            <span>
              <button type="button" class="btn sm" data-add-amp>
                Add from AMP${programmeFromPack ? ` · ${programmeFromPack}` : ''}
              </button>
              <button type="button" class="btn sm" data-add-pdf>
                Add from PDF${pdfLines.length ? ` · ${pdfLines.filter((p) => tickedPdf.has(p.id)).length}` : ''}
              </button>
            </span>
          </div>
          <div class="pick" data-items>${candidateRows()}</div>
        </div>
        <details class="card" style="margin-top:10px" data-cs-stan ${csStanOpen ? 'open' : ''}>
          <summary class="card-h">
            <div class="sum">
              <span class="twist" aria-hidden="true"></span>
              <div>
                <h2 style="font-size:13px">Embody a standard change</h2>
                <p class="tiny dim">
                  CS-STAN — nothing here is due; it is embodied because somebody chose to
                  ${tickedSpecs.size ? html` · <b>${tickedSpecs.size} selected</b>` : ''}
                </p>
              </div>
            </div>
          </summary>
          <div class="pick" data-cs-stan-list style="max-height:220px;overflow:auto">${csStanRows()}</div>
        </details>
        <div class="calc">
          <div><span class="lbl">Items</span><b>${t.count}</b></div>
          <div><span class="lbl">Estimated labour</span><b>${t.hours.toFixed(1)} h</b></div>
          ${ctx.labourRate > 0
            ? html`<div><span class="lbl">Chargeable at ${ctx.labourRate.toFixed(2)}/h</span><b>${money} ${esc(ctx.currency)}</b></div>`
            : html`<div><span class="lbl">Chargeable</span><b class="dim">no rate on file</b></div>`}
        </div>
        ${ctx.labourRate > 0
          ? ''
          : callout('caution', '!', html`
              No <b>engineering hour</b> on the organisation's rate card, so this visit prices at nothing —
              and when the release is signed, the labour will not be billed at all. Set the rate under
              <b>Organisation info → Rates</b>. It does not stop the pack being built or released.
            `)}
        ${callout('info', 'i', html`
          Pulling an item forward inside its tolerance band is free. Anything outside the band asks for
          a justification and records it against the task.
        `)}
      `;
    },

    onRender: (root, refresh) => {
      // Fetched once the dialog is up rather than before it opens: the pack is
      // usually built from what is due, and waiting on reference data nobody
      // asked for would delay every visit for the sake of the rare one.
      if (!csStanEntries.length && csStanEligible) {
        void loadCsStan(aircraftId).then(() => {
          if (csStanEntries.length) refresh();
        });
      }

      root.querySelector<HTMLSelectElement>('[data-aircraft]')?.addEventListener('change', async (e) => {
        aircraftId = (e.target as HTMLSelectElement).value;
        loading = true;
        refresh();
        const next = await api.planning.workPackContext(aircraftId);
        candidates = next.candidates;
        ticked = new Set(candidates.filter((c) => c.preselected).map(key));
        // Cleared with the aircraft. A standard change ticked against one
        // aircraft has no business travelling to another, and the server would
        // reject it anyway — silently, by dropping the line.
        tickedSpecs = new Set();
        csStanEntries = [];
        csStanEligible = true;
        loading = false;
        refresh();
        await loadCsStan(aircraftId);
        refresh();
      });

      root.querySelector('[data-add-amp]')?.addEventListener('click', (ev) => {
        ev.preventDefault();
        pickFromProgramme(refresh);
      });
      root.querySelector('[data-add-pdf]')?.addEventListener('click', (ev) => {
        ev.preventDefault();
        pickFromPdf(refresh);
      });
      root.querySelectorAll<HTMLInputElement>('[data-pdf-pick]').forEach((box) =>
        box.addEventListener('change', () => {
          const id = box.dataset.pdfPick!;
          box.checked ? tickedPdf.add(id) : tickedPdf.delete(id);
          refresh();
        }),
      );
      // The document's own name for itself, offered as the title once, where none was typed.
      const titleBox = root.querySelector<HTMLInputElement>('[name="title"]');
      if (titleBox && !titleBox.value && pdfTitle) titleBox.value = pdfTitle;

      root.querySelectorAll<HTMLInputElement>('[data-pick]').forEach((box) =>
        box.addEventListener('change', () => {
          const k = box.dataset.pick!;
          if (box.checked) ticked.add(k);
          else ticked.delete(k);
          refresh();
        }),
      );

      root.querySelector<HTMLDetailsElement>('[data-cs-stan]')?.addEventListener('toggle', (e) => {
        csStanOpen = (e.target as HTMLDetailsElement).open;
      });

      // The list is sixty-six long and scrolls. Re-rendering puts it back at
      // the top, which on a tick halfway down loses the reader's place just as
      // surely as folding it away did.
      const list = root.querySelector<HTMLElement>('[data-cs-stan-list]');
      if (list) {
        list.scrollTop = csStanScroll;
        list.addEventListener('scroll', () => { csStanScroll = list.scrollTop; });
      }

      root.querySelectorAll<HTMLInputElement>('[data-spec-pick]').forEach((box) =>
        box.addEventListener('change', () => {
          const id = box.dataset.specPick!;
          if (box.checked) tickedSpecs.add(id);
          else tickedSpecs.delete(id);
          refresh();
        }),
      );
    },

    collect: (root) => {
      const title = val(root, 'title');
      if (!title) return { error: 'Give the pack a title — it is what the hangar sees on the job card.' };

      const items: CreateWorkPack['items'] = candidates
        .filter((c) => ticked.has(key(c)))
        .map((c) => ({ kind: c.kind, refId: c.refId, manHours: c.manHours }));

      // No estimate: CS-STAN says what a change is, not how long it takes.
      // Left off so the hours on the pack stay the shop's own figures.
      for (const id of tickedSpecs) items.push({ kind: 'STANDARD_CHANGE', refId: id });

      // Lines off a document: their own words, an estimate only where the
      // document printed one.
      for (const p of pdfLines) {
        if (!tickedPdf.has(p.id)) continue;
        items.push({ kind: 'ADHOC', description: lineDescription(p.line), manHours: p.line.manHours ?? undefined });
      }

      if (!items.length) {
        return { error: 'Tick at least one item. A pack with nothing in it is a slot booking, not a work pack.' };
      }

      const start = val(root, 'plannedStart');
      const end = val(root, 'plannedEnd');
      if (start && end && end < start) return { error: 'The planned end is before the planned start.' };

      const [approval, org] = (val(root, 'performer') || '|').split('|');
      return {
        aircraftId,
        title,
        performingApproval: approval || undefined,
        performingOrg: org || undefined,
        plannedStart: start || undefined,
        plannedEnd: end || undefined,
        groundTimeDays: numVal(root, 'groundTimeDays'),
        items,
      };
    },

    submit: (payload) => api.planning.createWorkPack(payload),
    done: (r) => `${r.reference} created — ${r.itemCount} item(s), ${r.estimatedManHours.toFixed(1)} h on ${r.registration}`,
    onDone,
  });
}

/** Fetches the context, then opens. Used by every "New work pack" button. */
export async function newWorkPack(aircraftId?: string, onDone?: () => void): Promise<void> {
  const ctx = await api.planning.workPackContext(aircraftId);
  openNewWorkPack(ctx, onDone);
}
